import { useAtomValue, useSetAtom } from "jotai";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import useWorkspacePermissions from "../../../hooks/useWorkspacePermissions";
import usePagePermissions from "../../../hooks/usePagePermissions";
import { pageSettingsAtom } from "../../../atoms/PageAtoms";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import PageAPI from "../../api/PageAPI";
import { updateTenantUsers } from "../../../utils/UserUtils";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

const useAddPageUser = (pageID: string) => {
  const { cPermissions } = useCloudPermissions();
  const { wPermissions } = useWorkspacePermissions();
  const { pPermissions } = usePagePermissions(pageID);
  const queryClient = useQueryClient();
  const setSelectedPage = useSetAtom(pageSettingsAtom);
  const tenantID = useAtomValue(tenantIDAtom);

  const addPageUser = useMutation({
    mutationFn: ({
      workspaceID,
      pageID,
      userID,
      permissionID,
      onSuccess,
    }: {
      workspaceID: string;
      pageID: string;
      userID: string;
      permissionID: string;
      onSuccess?: () => void;
    }) => {
      if (
        !pPermissions.page_add_user &&
        !wPermissions.workspace_modify_page &&
        !cPermissions.cloud_modify_page
      ) {
        return Promise.reject(
          "User does not have permission to modify users in this page"
        );
      }

      return PageAPI.addPageUser(
        workspaceID,
        pageID,
        userID,
        permissionID
      ).then((data) => {
        const { user, page } = data.data.data;

        updateTenantUsers(queryClient, tenantID || "", user);
        queryClient.setQueryData(["page", pageID], page);

        // Selected page is the page opened in the page settings drawer.
        // Since you can only add users to a page that is currently open in the page settings drawer,
        // set the selected page to the page that was just updated.
        setSelectedPage(page);

        callFunctionIfExists(onSuccess);
      });
    },
  });

  return {
    addPageUser: addPageUser.mutate,
    addingUser: addPageUser.isPending,
    addPageUserError: addPageUser.error
      ? getErrorMessage(addPageUser.error)
      : null,
  };
};

export default useAddPageUser;
